import React, { FC, useEffect, useState } from 'react';
import ReactPlaceholder from 'react-placeholder/lib';
import { useDispatch, useSelector } from 'react-redux';
import setSelectedAbstractProductOperation from 'store/publicAssetProduct/operation/setSelectedAbstractProductOperation';
import getPublicAssetAbstractProductData from 'store/publicAssetProduct/selector/getPublicAssetAbstractProductData';
import getSelectedAbstractProduct from 'store/publicAssetProduct/selector/getSelectedAbstractProduct';
import getSelectedAbstractProductName from 'store/publicAssetProduct/selector/getSelectedAbstractProductName';
import { Product, PRODUCT_SLUGS } from 'store/publicAssetProduct/types';
import cn from 'classnames';
import CanvasIcon from 'components/Common/Icons/canvasIcon';
import PosterIcon from 'components/Common/Icons/posterIcon';
import MugIcon from 'components/Common/Icons/mugIcon';
import ToteBagIcon from 'components/Common/Icons/toteBagIcon';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import isDesktop from 'store/app/selector/isDesktop';
import OpIcon from 'components/Common/op-ui-wrappers/OpIcon/OpIcon';
import { useTranslation } from 'react-i18next';
import styles from './ProductSelector.module.scss';

const PRODUCTS_TO_SHOW = 8;

const PRODUCT_ICONS: { [key: string]: () => JSX.Element } = {
  [PRODUCT_SLUGS.canvas]: CanvasIcon,
  [PRODUCT_SLUGS.poster]: PosterIcon,
  [PRODUCT_SLUGS.mug]: MugIcon,
  [PRODUCT_SLUGS.toteBags]: ToteBagIcon,
};

const ProductIcon: FC<{ slug: string }> = ({ slug }) => {
  const Icon = PRODUCT_ICONS[slug];
  return Icon ? <Icon /> : null;
};
const ProductSelector = () => {
  const publicAssetAbstractProductData = useSelector(getPublicAssetAbstractProductData);
  const selectedAbstractProductName = useSelector(getSelectedAbstractProductName);
  const [selectedProductSlug, setSelectedProductSlug] = useState<string>();
  const isDesktopDevice = useSelector(isDesktop);
  const selectedAbstractProduct = useSelector(getSelectedAbstractProduct);
  const [page, setPage] = useState(1);
  const dispatch = useDispatch();

  const handleAbstractProductSelection = (product: Product) => {
    setSelectedProductSlug(product?.slug);
    dispatch(setSelectedAbstractProductOperation(product));
  };

  useEffect(() => {
    if (!selectedAbstractProduct || selectedAbstractProduct?.slug === selectedProductSlug) {
      return;
    }
    setSelectedProductSlug(selectedAbstractProduct?.slug);
  }, [selectedAbstractProduct]);

  const startIndex = (page - 1) * PRODUCTS_TO_SHOW;
  const endIndex = startIndex + PRODUCTS_TO_SHOW;
  const showRightCarret =
    publicAssetAbstractProductData?.products.length && publicAssetAbstractProductData?.products.length > endIndex;
  const showLeftCarret = page > 1;
  const { t } = useTranslation();

  return (
    <ReactPlaceholder
      showLoadingAnimation
      type="rect"
      ready={!!(publicAssetAbstractProductData && selectedAbstractProductName)}
      style={{ width: '100%', height: '40px', backgroundColor: '#e8e8e8' }}
    >
      <div className={styles.selectProduct}>
        {isDesktopDevice && showLeftCarret && (
          <OpIcon
            icon="carret-right"
            size="medium"
            className={cn(styles.carret, styles.carretLeft)}
            onClick={() => setPage(page - 1)}
          />
        )}
        {publicAssetAbstractProductData?.products.slice(startIndex, endIndex).map((product) => (
          <div
            className={cn(styles.productTile, {
              [styles.productTileSelected]: selectedProductSlug === product.slug,
            })}
            onClick={() => handleAbstractProductSelection(product)}
            key={product.productId}
          >
            {/* Icon hidden  */}
            {/* <div className={styles.productIcon}>
              <ProductIcon slug={product.slug} />
            </div> */}
            <div className={styles.productContent}>
              <OpText
                className={styles.productName}
                variant={isDesktopDevice ? 'body-medium-regular' : 'body-small-regular'}
              >
                {product.name}
              </OpText>

              <OpText variant="label-small-regular" className={styles.from}>
                {t('txt_pb_price_from')} &nbsp;
              </OpText>
              <OpText
                className={cn(styles.price, {
                  [styles.linethrough]: product.initialPrice.value !== product.finalPrice.value,
                })}
                variant="label-small-regular"
                dangerouslySetInnerHTML={{ __html: product.initialPrice.formatted || '' }}
              />
              {product.initialPrice.value !== product.finalPrice.value && (
                <OpText
                  className={styles.discountedPrice}
                  variant="label-small-regular"
                  dangerouslySetInnerHTML={{ __html: product.finalPrice.formatted || '' }}
                />
              )}
            </div>
          </div>
        ))}
        {isDesktopDevice && showRightCarret && (
          <OpIcon
            icon="carret-right"
            size="medium"
            className={cn(styles.carret, styles.carretRight)}
            onClick={() => setPage(page + 1)}
          />
        )}
      </div>
    </ReactPlaceholder>
  );
};

export default ProductSelector;
